import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import RouteRoundedIcon from "@mui/icons-material/RouteRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import WebRoundedIcon from "@mui/icons-material/WebRounded";
import { adminApi } from "../../api/admin";
import { useProjects } from "../../context/ProjectsContext";
import ContextualHelp from "../../components/dashboard/ContextualHelp";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";

const BRAND = {
  orange: "#F35A3A",
  dark: "var(--wp-panel-text-primary, #19242F)",
  green: "#16834B",
  bg: "var(--wp-panel-bg, #F6F7FB)",
};

const PERIODS = [
  { value: 7, label: "Últimos 7 días" },
  { value: 30, label: "Últimos 30 días" },
  { value: 90, label: "Últimos 90 días" },
  { value: 180, label: "Últimos 180 días" },
];

function formatNumber(value) {
  return new Intl.NumberFormat("es-AR").format(Number(value || 0));
}

function formatDecimal(value) {
  return new Intl.NumberFormat("es-AR", { maximumFractionDigits: 1 }).format(Number(value || 0));
}

function formatDate(value, withTime = false) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", withTime
    ? { dateStyle: "short", timeStyle: "short" }
    : { dateStyle: "short" }).format(date);
}

function formatDuration(value) {
  const ms = Number(value || 0);
  if (!ms) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${formatDecimal(seconds)} s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${formatDecimal(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 24) return `${formatDecimal(hours)} h`;
  return `${formatDecimal(hours / 24)} días`;
}

function statusTone(value) {
  if (value >= 70) return { color: "error", label: "Crítico" };
  if (value >= 45) return { color: "warning", label: "Revisar" };
  if (value >= 20) return { color: "info", label: "Atención" };
  return { color: "success", label: "Saludable" };
}

function MetricCard({ icon, label, value, helper, accent = "orange" }) {
  const palette = {
    orange: { bg: "rgba(243,90,58,.10)", fg: BRAND.orange },
    green: { bg: "rgba(22,131,75,.10)", fg: BRAND.green },
    dark: { bg: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", fg: BRAND.dark },
    warning: { bg: "rgba(237,108,2,.10)", fg: "#B85B00" },
  };
  const tone = palette[accent] || palette.orange;
  return (
    <Card elevation={0} sx={{ height: "100%", borderRadius: { xs: 3, sm: 4 }, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
      <CardContent sx={{ p: { xs: 1.8, sm: 2.5 }, "&:last-child": { pb: { xs: 1.8, sm: 2.5 } } }}>
        <Stack direction="row" spacing={{ xs: 1.1, sm: 1.5 }} alignItems="flex-start">
          <Box sx={{ width: { xs: 40, sm: 46 }, height: { xs: 40, sm: 46 }, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: tone.bg, color: tone.fg, flex: "0 0 auto" }}>
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 950, letterSpacing: .35 }}>
              {label}
            </Typography>
            <Typography variant="h4" sx={{ mt: .15, color: BRAND.dark, fontWeight: 950, letterSpacing: "-.035em", fontSize: { xs: 26, sm: 34 }, lineHeight: 1.1, overflowWrap: "anywhere" }}>
              {value}
            </Typography>
            <Typography variant="caption" sx={{ display: { xs: "none", sm: "block" }, mt: .2, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", lineHeight: 1.35 }}>
              {helper}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SectionHeader({ icon, title, description, helpText = "", action }) {
  return (
    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={{ xs: 1.2, sm: 1.5 }} alignItems={{ md: "center" }}>
      <Stack direction="row" spacing={1.2} alignItems="flex-start" sx={{ minWidth: 0 }}>
        <Box sx={{ width: { xs: 36, sm: 40 }, height: { xs: 36, sm: 40 }, borderRadius: 2.3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.09)", color: BRAND.orange, flex: "0 0 auto" }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.7} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h5" sx={{ color: BRAND.dark, fontWeight: 950, letterSpacing: "-.025em", fontSize: { xs: 20, sm: 24 }, lineHeight: 1.15 }}>
              {title}
            </Typography>
            {helpText ? <ContextualHelp compact title={`Cómo leer ${title}`} text={helpText} /> : null}
          </Stack>
          {description ? (
            <Typography variant="body2" sx={{ mt: .35, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", maxWidth: 760, fontSize: { xs: 13, sm: 14 }, lineHeight: 1.45 }}>
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {action}
    </Stack>
  );
}

function FunnelCard({ stage, index }) {
  const width = Math.max(0, Math.min(100, Number(stage.conversionFromStart || 0)));
  const loss = Number(stage.dropOffRate || 0);
  const entered = Number(stage.entered ?? stage.count ?? 0);
  const advanced = Number(stage.advanced ?? stage.count ?? 0);
  const abandoned = Number(stage.abandoned ?? stage.dropOff ?? 0);
  const sampleSize = Number(stage.timeSampleSize || 0);

  return (
    <Box sx={{ p: { xs: 1.45, sm: 2 }, borderRadius: 3, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", bgcolor: "var(--wp-panel-surface, #fff)" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 1.15, sm: 1.5 }} alignItems={{ md: "center" }}>
        <Box sx={{ width: { md: 270 }, flex: "0 0 auto" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 30, height: 30, borderRadius: 999, display: "grid", placeItems: "center", bgcolor: index === 0 ? BRAND.dark : BRAND.orange, color: index === 0 ? "#fff" : "#17181C", fontWeight: 950, fontSize: 13 }}>
              {index + 1}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark, lineHeight: 1.15 }}>{stage.label}</Typography>
              <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                {index === 0
                  ? `${formatNumber(advanced)} cliente${advanced === 1 ? "" : "s"}`
                  : `${formatNumber(advanced)} avanzaron de ${formatNumber(entered)}`}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ height: 13, borderRadius: 99, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.07)", overflow: "hidden" }}>
            <Box sx={{ width: `${width}%`, minWidth: advanced ? 8 : 0, height: "100%", borderRadius: 99, bgcolor: index === 0 ? BRAND.dark : BRAND.orange, transition: "width .25s ease" }} />
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: .8 }}>
            <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", fontWeight: 800 }}>
              {formatDecimal(stage.conversionFromStart)}% desde el alta
            </Typography>
            {index > 0 ? (
              <Typography variant="caption" sx={{ color: loss >= 35 ? "error.main" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", fontWeight: 850 }}>
                {formatDecimal(stage.conversionFromPrevious)}% avanzó desde el paso anterior
              </Typography>
            ) : null}
            {sampleSize > 0 ? (
              <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", fontWeight: 800 }}>
                mediana {formatDuration(stage.medianFromPreviousMs)} · P90 {formatDuration(stage.p90FromPreviousMs)} · {formatNumber(sampleSize)} muestras
              </Typography>
            ) : null}
          </Stack>
        </Box>

        {index > 0 ? (
          <Box sx={{ display: { xs: "flex", md: "block" }, justifyContent: "space-between", alignItems: "center", width: { md: 160 }, textAlign: { md: "right" }, flex: "0 0 auto", gap: 1 }}>
            <Chip
              size="small"
              color={loss >= 40 ? "error" : loss >= 20 ? "warning" : "success"}
              label={`Fuga ${formatDecimal(loss)}%`}
              sx={{ fontWeight: 950 }}
            />
            <Typography variant="caption" sx={{ display: "block", mt: { xs: 0, md: .4 }, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", textAlign: { xs: "right", md: "inherit" } }}>
              {formatNumber(abandoned)} no avanzaron
            </Typography>
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}

function DailyPulse({ rows = [] }) {
  const visibleRows = rows.length > 45 ? rows.slice(-45) : rows;
  const maximum = Math.max(1, ...visibleRows.flatMap((row) => [row.registrations, row.firstEdits, row.firstItems, row.firstVisits]));
  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, auto)" }, gap: { xs: .8, sm: 2 }, mb: 1.5 }}>
        {[
          ["Altas", BRAND.dark],
          ["Primeras ediciones", BRAND.orange],
          ["Primeros ítems", BRAND.green],
          ["Primeras visitas", "#5673C8"],
        ].map(([label, color]) => (
          <Stack key={label} direction="row" spacing={.7} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: 999, bgcolor: color }} />
            <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 800 }}>{label}</Typography>
          </Stack>
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: .45, alignItems: "flex-end", height: { xs: 120, sm: 150 }, overflowX: "auto", pb: 1, WebkitOverflowScrolling: "touch" }}>
        {visibleRows.map((row, index) => (
          <Tooltip
            key={row.date}
            title={`${formatDate(row.date)} · ${row.registrations} altas · ${row.firstEdits} ediciones · ${row.firstItems} ítems · ${row.firstVisits} visitas`}
          >
            <Box sx={{ minWidth: visibleRows.length > 30 ? 11 : 18, flex: 1, height: "100%", display: "flex", alignItems: "flex-end", gap: .15 }}>
              {[
                [row.registrations, BRAND.dark],
                [row.firstEdits, BRAND.orange],
                [row.firstItems, BRAND.green],
                [row.firstVisits, "#5673C8"],
              ].map(([value, color], metricIndex) => (
                <Box
                  key={`${row.date}-${metricIndex}`}
                  sx={{
                    flex: 1,
                    minHeight: value ? 4 : 1,
                    height: `${Math.max(value ? 4 : 1, (value / maximum) * 100)}%`,
                    bgcolor: color,
                    borderRadius: "3px 3px 0 0",
                    opacity: value ? .92 : .12,
                  }}
                />
              ))}
              {index === visibleRows.length - 1 ? null : null}
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
}

function ScoreChip({ score }) {
  const tone = statusTone(Number(score || 0));
  return <Chip size="small" color={tone.color} label={`${tone.label} · ${formatNumber(score)}`} sx={{ fontWeight: 900 }} />;
}

function EmptyRow({ columns, text }) {
  return (
    <TableRow>
      <TableCell colSpan={columns} align="center" sx={{ py: 5, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
        {text}
      </TableCell>
    </TableRow>
  );
}

function MobileValueGrid({ items }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: .85, mt: 1.25 }}>
      {items.map(([label, value, tone]) => (
        <Box key={label} sx={{ p: 1, borderRadius: 2, bgcolor: tone || "rgba(var(--wp-panel-ink-rgb, 25,36,47),.045)", minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 850 }}>{label}</Typography>
          <Typography sx={{ mt: .15, color: BRAND.dark, fontWeight: 950, overflowWrap: "anywhere" }}>{value}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function MobileScreenCard({ screen }) {
  return (
    <Box sx={{ p: 1.45, borderRadius: 2.8, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", bgcolor: "var(--wp-panel-surface, #fff)" }}>
      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 950, color: BRAND.dark, lineHeight: 1.2 }}>{screen.label}</Typography>
          <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", overflowWrap: "anywhere" }}>{screen.path}</Typography>
        </Box>
        <ScoreChip score={screen.frictionScore} />
      </Stack>
      <MobileValueGrid items={[
        ["Vistas", formatNumber(screen.views)],
        ["Usuarios", formatNumber(screen.uniqueUsers)],
        ["Mediana", formatDuration(screen.medianDurationMs)],
        ["P90", formatDuration(screen.p90DurationMs)],
        ["Fin de sesión", `${formatDecimal(screen.endRate)}%`, screen.endRate >= 45 ? "rgba(211,47,47,.08)" : screen.endRate >= 25 ? "rgba(237,108,2,.08)" : undefined],
        ["Reingreso", `${formatDecimal(screen.repeatRate)}%`],
      ]} />
    </Box>
  );
}

function MobileOperationCard({ operation }) {
  return (
    <Box sx={{ p: 1.45, borderRadius: 2.8, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", bgcolor: "var(--wp-panel-surface, #fff)" }}>
      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 950, color: BRAND.dark, lineHeight: 1.2 }}>{operation.label}</Typography>
          <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{operation.category} · {formatNumber(operation.uniqueUsers)} usuarios</Typography>
        </Box>
        <ScoreChip score={operation.frictionScore} />
      </Stack>
      <MobileValueGrid items={[
        ["Usos", formatNumber(operation.count)],
        ["Errores", `${formatDecimal(operation.failRate)}%`, operation.failRate >= 20 ? "rgba(211,47,47,.08)" : operation.failRate > 0 ? "rgba(237,108,2,.08)" : undefined],
        ["Mediana", formatDuration(operation.medianDurationMs)],
        ["P90", formatDuration(operation.p90DurationMs)],
      ]} />
    </Box>
  );
}

function MobileCustomerCard({ customer }) {
  const isRisk = String(customer.stalledReason || "").startsWith("Frenado");
  const activated = customer.firstEditAt && customer.firstItemAt;
  return (
    <Box sx={{ p: 1.45, borderRadius: 2.8, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", bgcolor: "var(--wp-panel-surface, #fff)" }}>
      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{customer.name || "Sin nombre"}</Typography>
          <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", overflowWrap: "anywhere" }}>{customer.email}</Typography>
          <Typography variant="caption" sx={{ display: "block", color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Alta {formatDate(customer.registeredAt)}</Typography>
        </Box>
        <Chip size="small" color={activated ? "success" : isRisk ? "warning" : "info"} label={customer.currentStageLabel} sx={{ fontWeight: 850, maxWidth: 130, "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }} />
      </Stack>

      <Box sx={{ mt: 1.15, p: 1.1, borderRadius: 2.2, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.04)" }}>
        <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 850 }}>PROYECTO</Typography>
        <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{customer.projectName || "Todavía sin proyecto"}</Typography>
        <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{customer.projectSlug || "—"}</Typography>
      </Box>

      <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mt: 1.2 }}>
        <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 800 }}>Progreso</Typography>
        <Typography sx={{ fontWeight: 950 }}>{formatNumber(customer.progress)}%</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={customer.progress} color={isRisk ? "warning" : activated ? "success" : "primary"} sx={{ mt: .55, height: 7, borderRadius: 99 }} />

      <MobileValueGrid items={[
        ["Ediciones", formatNumber(customer.landingEdits)],
        ["Ítems", formatNumber(customer.itemCount)],
      ]} />

      <Stack direction="row" spacing={.7} alignItems="flex-start" sx={{ mt: 1.15 }}>
        {activated ? <CheckCircleRoundedIcon color="success" fontSize="small" /> : isRisk ? <ErrorOutlineRoundedIcon color="warning" fontSize="small" /> : <TimerRoundedIcon color="info" fontSize="small" />}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 800, color: isRisk ? "warning.dark" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.65)", lineHeight: 1.35 }}>{customer.stalledReason}</Typography>
          {!activated ? <Typography variant="caption" sx={{ display: "block", mt: .25, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Sigue: {customer.nextStageLabel}</Typography> : null}
          <Typography variant="caption" sx={{ display: "block", mt: .45, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Última actividad {formatDate(customer.lastActivityAt, true)} · {formatNumber(customer.daysSinceSignup)} días desde el alta</Typography>
        </Box>
      </Stack>
    </Box>
  );
}

export default function SuperAnalyticsPage() {
  const { projects, loadProjects } = useProjects();
  const [days, setDays] = React.useState(30);
  const [project, setProject] = React.useState("");
  const [analytics, setAnalytics] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [customerSearch, setCustomerSearch] = React.useState("");
  const [customerState, setCustomerState] = React.useState("all");

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [loadProjects, projects.length]);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await adminApi.activityAnalytics({
        days,
        ...(project ? { project } : {}),
      });
      setAnalytics(response.analytics || null);
    } catch (err) {
      setError(err.message || "No se pudo cargar la analítica global");
    } finally {
      setLoading(false);
    }
  }, [days, project]);

  React.useEffect(() => {
    load();
  }, [load]);

  const overview = analytics?.overview || {};
  const funnel = analytics?.funnel || [];
  const screens = analytics?.screens || [];
  const operations = analytics?.operations || [];
  const transitions = analytics?.transitions || [];
  const customers = analytics?.customers || [];
  const biggestDrop = analytics?.biggestDrop || null;
  const conversions = analytics?.conversions || {};
  const coverage = analytics?.coverage || {};
  const appliedFilters = analytics?.filters || {};
  const appliedPeriod = analytics?.period || {};

  const filteredCustomers = React.useMemo(() => {
    const query = customerSearch.trim().toLowerCase();
    return customers.filter((customer) => {
      const matchesSearch = !query || [customer.name, customer.email, customer.projectName, customer.projectSlug]
        .some((value) => String(value || "").toLowerCase().includes(query));
      const isRisk = String(customer.stalledReason || "").startsWith("Frenado");
      const isActivated = customer.firstEditAt && customer.firstItemAt;
      const matchesState = customerState === "all"
        || (customerState === "risk" && isRisk)
        || (customerState === "activated" && isActivated)
        || (customerState === "building" && !isRisk && !isActivated);
      return matchesSearch && matchesState;
    });
  }, [customerSearch, customerState, customers]);

  if (loading && !analytics) {
    return <DashboardLoadingState label="Cargando analítica global…" minHeight={420} />;
  }

  return (
    <Stack spacing={{ xs: 1.8, sm: 3 }} sx={{ minWidth: 0 }}>
      <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" spacing={{ xs: 1.4, sm: 2 }} alignItems={{ lg: "center" }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={{ xs: 1, sm: 1.2 }} alignItems="flex-start">
            <Box sx={{ width: { xs: 42, sm: 50 }, height: { xs: 42, sm: 50 }, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange, flexShrink: 0 }}>
              <AnalyticsRoundedIcon sx={{ fontSize: { xs: 26, sm: 35 } }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h3" sx={{ color: BRAND.dark, fontWeight: 950, letterSpacing: "-.04em", lineHeight: 1.05, fontSize: { xs: 27, sm: 38 } }}>
                Embudo y analítica global
              </Typography>
              <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: .45 }}>
                <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", maxWidth: 980, fontSize: { xs: 13.5, sm: 16 }, lineHeight: 1.45 }}>
                  Altas, activación, fricción y clientes en riesgo.
                </Typography>
                <ContextualHelp compact title="Cómo leer la analítica" text="Primero revisá la mayor fuga del embudo; después pantallas con alto fin de sesión y reingreso; por último operaciones con P90 alto o errores." />
              </Stack>
            </Box>
          </Stack>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} flexWrap="wrap" useFlexGap sx={{ width: { xs: "100%", lg: "auto" } }}>
          <FormControl fullWidth size="small" sx={{ minWidth: { sm: 220 }, width: { sm: "auto" } }}>
            <InputLabel>Proyecto</InputLabel>
            <Select value={project} label="Proyecto" onChange={(event) => setProject(event.target.value)} sx={{ borderRadius: 999, bgcolor: "var(--wp-panel-surface, #fff)", fontWeight: 850 }}>
              <MenuItem value="">Todos los proyectos</MenuItem>
              {[...projects]
                .sort((left, right) => String(left.name || left.slug || "").localeCompare(String(right.name || right.slug || ""), "es"))
                .map((item) => {
                  const value = item.slug || item.id || item._id;
                  return <MenuItem key={item.id || item._id || item.slug} value={value}>{item.name || item.slug}</MenuItem>;
                })}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ minWidth: { sm: 190 }, width: { sm: "auto" } }}>
            <InputLabel>Período de altas</InputLabel>
            <Select value={days} label="Período de altas" onChange={(event) => setDays(Number(event.target.value))} sx={{ borderRadius: 999, bgcolor: "var(--wp-panel-surface, #fff)", fontWeight: 850 }}>
              {PERIODS.map((period) => <MenuItem key={period.value} value={period.value}>{period.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={loading ? <WebProfitSpinner size={17} /> : <RefreshRoundedIcon />}
            onClick={load}
            disabled={loading}
            fullWidth
            sx={{ width: { sm: "auto" }, borderRadius: 999, textTransform: "none", fontWeight: 900, minHeight: 42 }}
          >
            Actualizar
          </Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {!error && analytics ? (
        <Stack direction="row" spacing={.8} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            variant="outlined"
            label={appliedFilters.projectName || "Todos los proyectos"}
            sx={{ fontWeight: 850, bgcolor: "var(--wp-panel-surface, #fff)" }}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`${formatDate(appliedPeriod.since)} → ${formatDate(appliedPeriod.until)}`}
            sx={{ fontWeight: 850, bgcolor: "var(--wp-panel-surface, #fff)" }}
          />
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label="Embudo consolidado por backend"
            sx={{ fontWeight: 850, bgcolor: "rgba(22,131,75,.03)" }}
          />
        </Stack>
      ) : null}
      {coverage.cohortLimited || coverage.cohortEventsLimited || coverage.routeEventsLimited || coverage.operationEventsLimited ? (
        <Alert severity="warning">
          El período alcanzó el límite de seguridad de procesamiento. Las tendencias siguen siendo útiles, pero conviene consultar un rango más corto para máxima precisión.
        </Alert>
      ) : null}

      <Grid container spacing={{ xs: 1.4, sm: 2.3 }}>
        <Grid item xs={6} xl={3}>
          <MetricCard icon={<PersonAddAlt1RoundedIcon />} label="CUENTAS NUEVAS" value={loading ? "—" : formatNumber(overview.registrations)} helper={project ? `${formatNumber(coverage.totalClients)} clientes vinculados al proyecto` : `${formatNumber(coverage.totalClients)} clientes acumulados en WebProfit`} accent="dark" />
        </Grid>
        <Grid item xs={6} xl={3}>
          <MetricCard icon={<RocketLaunchRoundedIcon />} label="ACTIVACIÓN REAL" value={loading ? "—" : `${formatDecimal(overview.activationRate)}%`} helper={`${formatNumber(overview.activated)} cargaron una edición y al menos un ítem`} accent="green" />
        </Grid>
        <Grid item xs={6} xl={3}>
          <MetricCard icon={<TimerRoundedIcon />} label="TIEMPO A ACTIVACIÓN" value={loading ? "—" : formatDuration(overview.medianActivationMs)} helper="Mediana desde el registro hasta editar la web y cargar un ítem" accent="orange" />
        </Grid>
        <Grid item xs={6} xl={3}>
          <MetricCard icon={<WarningAmberRoundedIcon />} label="CLIENTES FRENADOS" value={loading ? "—" : formatNumber(overview.atRisk)} helper={`${formatDecimal(overview.atRiskRate)}% superó el tiempo normal de su paso actual`} accent="warning" />
        </Grid>
      </Grid>

      {biggestDrop ? (
        <Alert
          severity={biggestDrop.dropOffRate >= 40 ? "error" : "warning"}
          icon={<FilterAltRoundedIcon />}
          sx={{ borderRadius: 3, alignItems: "center", px: { xs: 1.35, sm: 2 }, py: { xs: .75, sm: 1 } }}
        >
          <Typography sx={{ fontWeight: 950 }}>
            Mayor fuga: {biggestDrop.fromLabel} → {biggestDrop.toLabel || biggestDrop.label}
          </Typography>
          <Typography variant="body2">
            Se pierden {formatNumber(biggestDrop.abandoned ?? biggestDrop.dropOff)} clientes ({formatDecimal(biggestDrop.abandonmentRate ?? biggestDrop.dropOffRate)}%). El tiempo mediano para quienes sí avanzan es {formatDuration(biggestDrop.medianMs ?? biggestDrop.medianFromPreviousMs)}.
          </Typography>
        </Alert>
      ) : null}

      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, borderRadius: { xs: 3, sm: 4 }, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
        <SectionHeader
          icon={<RouteRoundedIcon />}
          title="Embudo completo de activación"
          description="Conversión y abandono entre los pasos de activación."
          helpText="Los pasos y tiempos llegan consolidados desde backend. El panel no vuelve a agrupar eventos ni recalcula el embudo, lo que evita duplicados por navegación o reintentos."
          action={!loading && conversions.startCount ? (
            <Chip
              color="success"
              label={`${formatDecimal(conversions.overallRate)}% de punta a punta · ${formatNumber(conversions.completedCount)} de ${formatNumber(conversions.startCount)}`}
              sx={{ fontWeight: 950 }}
            />
          ) : null}
        />
        <Stack spacing={1.15} sx={{ mt: { xs: 1.6, sm: 2.5 } }}>
          {loading ? <WebProfitSpinner size={24} sx={{ mx: "auto", my: 1 }} /> : null}
          {!loading && !funnel.length ? <Alert severity="info">No hay datos de embudo para los filtros seleccionados.</Alert> : null}
          {!loading ? funnel.map((stage, index) => <FunnelCard key={stage.key} stage={stage} index={index} />) : null}
        </Stack>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid item xs={12} xl={8}>
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: "100%", borderRadius: { xs: 3, sm: 4 }, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
            <SectionHeader
              icon={<AutoGraphRoundedIcon />}
              title="Pulso diario del tobogán"
              description="Altas y primeras acciones, día por día."
              helpText="Compara altas, primeras ediciones, primeros ítems y primeras visitas para comprobar si los cambios de onboarding ayudan a que más clientes avancen."
            />
            <Box sx={{ mt: { xs: 1.6, sm: 2.5 } }}>
              {loading ? <WebProfitSpinner size={24} sx={{ mx: "auto", my: 1 }} /> : <DailyPulse rows={analytics?.daily || []} />}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} xl={4}>
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: "100%", borderRadius: { xs: 3, sm: 4 }, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
            <SectionHeader
              icon={<SpeedRoundedIcon />}
              title="Profundidad de uso"
              description="Promedios sobre la cohorte seleccionada."
            />
            <Stack spacing={{ xs: 1.65, sm: 2.2 }} sx={{ mt: { xs: 1.8, sm: 3 } }}>
              <Box>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", fontWeight: 800 }}>Ítems por cliente activado</Typography>
                  <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>{formatDecimal(overview.averageItemsPerActivated)}</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={Math.min(100, Number(overview.averageItemsPerActivated || 0) * 10)} sx={{ mt: 1, height: 8, borderRadius: 99 }} />
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", fontWeight: 800 }}>Ediciones por cuenta nueva</Typography>
                  <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>{formatDecimal(overview.averageLandingEdits)}</Typography>
                </Stack>
                <LinearProgress color="success" variant="determinate" value={Math.min(100, Number(overview.averageLandingEdits || 0) * 12.5)} sx={{ mt: 1, height: 8, borderRadius: 99 }} />
              </Box>
              <Divider />
              <Stack spacing={1.2}>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Abrieron el editor</Typography><Typography sx={{ fontWeight: 950 }}>{formatNumber(overview.editorUsers)}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Guardaron una edición</Typography><Typography sx={{ fontWeight: 950 }}>{formatNumber(overview.firstEdits)}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Cargaron un ítem</Typography><Typography sx={{ fontWeight: 950 }}>{formatNumber(overview.firstItems)}</Typography></Stack>
              </Stack>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, borderRadius: { xs: 3, sm: 4 }, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
        <SectionHeader
          icon={<WebRoundedIcon />}
          title="Pantallas donde la gente se queda o abandona"
          description="Pantallas con señales de fricción o abandono."
          helpText="La prioridad combina finales de sesión, permanencias largas, reingresos y duración alta. Mucho tiempo por sí solo no implica un problema: pesa más cuando también hay abandonos o vueltas repetidas."
        />
        <Stack spacing={1.1} sx={{ display: { xs: "flex", md: "none" }, mt: 1.6 }}>
          {!loading && !screens.length ? <Alert severity="info">Todavía no hay suficientes recorridos para comparar pantallas.</Alert> : null}
          {screens.slice(0, 15).map((screen) => <MobileScreenCard key={screen.path} screen={screen} />)}
        </Stack>
        <TableContainer sx={{ display: { xs: "none", md: "block" }, mt: 2.2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 950 }}>Pantalla</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950 }}>Vistas</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950 }}>Usuarios</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950 }}>Mediana</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950 }}>P90</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950 }}>Fin de sesión</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950 }}>Reingreso</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950 }}>Prioridad</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && !screens.length ? <EmptyRow columns={8} text="Todavía no hay suficientes recorridos para comparar pantallas." /> : null}
              {screens.slice(0, 15).map((screen) => (
                <TableRow key={screen.path} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{screen.label}</Typography>
                    <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{screen.path}</Typography>
                  </TableCell>
                  <TableCell align="right">{formatNumber(screen.views)}</TableCell>
                  <TableCell align="right">{formatNumber(screen.uniqueUsers)}</TableCell>
                  <TableCell align="right">{formatDuration(screen.medianDurationMs)}</TableCell>
                  <TableCell align="right">{formatDuration(screen.p90DurationMs)}</TableCell>
                  <TableCell align="right"><Chip size="small" label={`${formatDecimal(screen.endRate)}%`} color={screen.endRate >= 45 ? "error" : screen.endRate >= 25 ? "warning" : "default"} sx={{ fontWeight: 850 }} /></TableCell>
                  <TableCell align="right">{formatDecimal(screen.repeatRate)}%</TableCell>
                  <TableCell align="right"><ScoreChip score={screen.frictionScore} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid item xs={12} xl={7}>
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: "100%", borderRadius: { xs: 3, sm: 4 }, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
            <SectionHeader
              icon={<SpeedRoundedIcon />}
              title="Ediciones y operaciones que más se traban"
              description="Operaciones con más demora, errores o usuarios afectados."
              helpText="La prioridad cruza duración real, errores y cantidad de usuarios afectados. Las ediciones del constructor se separan por sección o ajuste global para no mezclar causas distintas."
            />
            <Stack spacing={1.1} sx={{ display: { xs: "flex", md: "none" }, mt: 1.6 }}>
              {!loading && !operations.length ? <Alert severity="info">Todavía no hay operaciones suficientes para medir fricción.</Alert> : null}
              {operations.slice(0, 16).map((operation) => <MobileOperationCard key={operation.key} operation={operation} />)}
            </Stack>
            <TableContainer sx={{ display: { xs: "none", md: "block" }, mt: 2.2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 950 }}>Operación</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 950 }}>Usos</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 950 }}>Mediana</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 950 }}>P90</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 950 }}>Errores</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 950 }}>Prioridad</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && !operations.length ? <EmptyRow columns={6} text="Todavía no hay operaciones suficientes para medir fricción." /> : null}
                  {operations.slice(0, 16).map((operation) => (
                    <TableRow key={operation.key} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{operation.label}</Typography>
                        <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{operation.category} · {formatNumber(operation.uniqueUsers)} usuarios</Typography>
                      </TableCell>
                      <TableCell align="right">{formatNumber(operation.count)}</TableCell>
                      <TableCell align="right">{formatDuration(operation.medianDurationMs)}</TableCell>
                      <TableCell align="right">{formatDuration(operation.p90DurationMs)}</TableCell>
                      <TableCell align="right">
                        <Chip size="small" color={operation.failRate >= 20 ? "error" : operation.failRate > 0 ? "warning" : "success"} label={`${formatDecimal(operation.failRate)}%`} sx={{ fontWeight: 850 }} />
                      </TableCell>
                      <TableCell align="right"><ScoreChip score={operation.frictionScore} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} xl={5}>
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: "100%", borderRadius: { xs: 3, sm: 4 }, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
            <SectionHeader
              icon={<TimerRoundedIcon />}
              title="Tiempo entre operación y operación"
              description="Pausas entre acciones consecutivas."
              helpText="Mide el tiempo entre dos acciones exitosas del mismo usuario y proyecto. Las pausas largas ayudan a localizar decisiones difíciles, formularios extensos o pasos poco claros."
            />
            <Stack spacing={1.1} sx={{ mt: 2.2 }}>
              {!loading && !transitions.length ? (
                <Alert severity="info">Todavía no hay secuencias repetidas suficientes para comparar tiempos.</Alert>
              ) : null}
              {transitions.slice(0, 12).map((transition, index) => (
                <Box key={transition.key} sx={{ p: { xs: 1.35, sm: 1.6 }, borderRadius: 2.5, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", bgcolor: index < 3 ? "rgba(243,90,58,.035)" : "var(--wp-panel-surface, #fff)" }}>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={{ xs: .8, sm: 1.5 }} alignItems="flex-start">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: BRAND.orange, fontWeight: 950 }}>DE → A</Typography>
                      <Typography sx={{ color: BRAND.dark, fontWeight: 900, lineHeight: 1.3 }}>{transition.fromLabel}</Typography>
                      <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{transition.toLabel}</Typography>
                    </Box>
                    <Box sx={{ textAlign: { xs: "left", sm: "right" }, flex: "0 0 auto", display: { xs: "flex", sm: "block" }, gap: 1, alignItems: "baseline" }}>
                      <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>{formatDuration(transition.medianGapMs)}</Typography>
                      <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{formatNumber(transition.count)} secuencias</Typography>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, borderRadius: { xs: 3, sm: 4 }, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
        <SectionHeader
          icon={<Inventory2RoundedIcon />}
          title="Clientes dentro del embudo"
          description="Cuentas que necesitan atención para seguir avanzando."
          helpText="Usá esta vista para identificar clientes concretos que pueden necesitar una mejora de interfaz, ayuda adicional o seguimiento comercial según el punto donde se frenaron."
          action={(
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ minWidth: { md: 430 }, width: { xs: "100%", md: "auto" } }}>
              <TextField size="small" fullWidth value={customerSearch} onChange={(event) => setCustomerSearch(event.target.value)} placeholder="Buscar cliente o proyecto" />
              <FormControl fullWidth size="small" sx={{ minWidth: { sm: 160 }, width: { sm: "auto" } }}>
                <InputLabel>Estado</InputLabel>
                <Select value={customerState} label="Estado" onChange={(event) => setCustomerState(event.target.value)}>
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="risk">Frenados</MenuItem>
                  <MenuItem value="building">Avanzando</MenuItem>
                  <MenuItem value="activated">Activados</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}
        />
        <Stack spacing={1.1} sx={{ display: { xs: "flex", md: "none" }, mt: 1.6 }}>
          {!loading && !filteredCustomers.length ? <Alert severity="info">No hay clientes que coincidan con el filtro.</Alert> : null}
          {filteredCustomers.map((customer) => <MobileCustomerCard key={customer.userId} customer={customer} />)}
        </Stack>
        <TableContainer sx={{ display: { xs: "none", md: "block" }, mt: 2.2, maxHeight: 620 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 950 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 950 }}>Proyecto</TableCell>
                <TableCell sx={{ fontWeight: 950 }}>Paso actual</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950 }}>Progreso</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950 }}>Ediciones</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950 }}>Ítems</TableCell>
                <TableCell sx={{ fontWeight: 950 }}>Diagnóstico</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950 }}>Última actividad</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && !filteredCustomers.length ? <EmptyRow columns={8} text="No hay clientes que coincidan con el filtro." /> : null}
              {filteredCustomers.map((customer) => {
                const isRisk = String(customer.stalledReason || "").startsWith("Frenado");
                const activated = customer.firstEditAt && customer.firstItemAt;
                return (
                  <TableRow key={customer.userId} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{customer.name || "Sin nombre"}</Typography>
                      <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{customer.email}</Typography>
                      <Typography variant="caption" sx={{ display: "block", color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Alta {formatDate(customer.registeredAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 850 }}>{customer.projectName || "Todavía sin proyecto"}</Typography>
                      <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{customer.projectSlug || "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={activated ? "success" : isRisk ? "warning" : "info"} label={customer.currentStageLabel} sx={{ fontWeight: 850 }} />
                      {!activated ? <Typography variant="caption" sx={{ display: "block", mt: .45, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Sigue: {customer.nextStageLabel}</Typography> : null}
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 120 }}>
                      <Typography sx={{ fontWeight: 900 }}>{formatNumber(customer.progress)}%</Typography>
                      <LinearProgress variant="determinate" value={customer.progress} color={isRisk ? "warning" : activated ? "success" : "primary"} sx={{ mt: .5, height: 6, borderRadius: 99 }} />
                    </TableCell>
                    <TableCell align="right">{formatNumber(customer.landingEdits)}</TableCell>
                    <TableCell align="right">{formatNumber(customer.itemCount)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={.7} alignItems="center">
                        {activated ? <CheckCircleRoundedIcon color="success" fontSize="small" /> : isRisk ? <ErrorOutlineRoundedIcon color="warning" fontSize="small" /> : <TimerRoundedIcon color="info" fontSize="small" />}
                        <Typography variant="body2" sx={{ fontWeight: 750, color: isRisk ? "warning.dark" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.65)" }}>{customer.stalledReason}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatDate(customer.lastActivityAt, true)}</Typography>
                      <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{formatNumber(customer.daysSinceSignup)} días desde el alta</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <ContextualHelp
          compact
          title="Orden sugerido de revisión"
          text="Primero la mayor fuga del embudo; después pantallas con alto fin de sesión y reingreso; por último operaciones con P90 alto o errores."
        />
      </Box>
    </Stack>
  );
}
